import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { Button, Table, TableRow } from '../components/ui';
import 'react-tabs/style/react-tabs.css';
import api from '../services/api';
import IconButton from '../components/IconButton';
import DateInput from '../components/DateInput';
import { useAuth } from '../context/AuthContext';

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [viewMode, setViewMode] = useState(null); // 'view' or 'edit' or null
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exporting, setExporting] = useState(false);



  // Contacts navigation
  const navigate = useNavigate();
  const navigateToContacts = (client) => {
    // use router navigation to preserve SPA behavior
    navigate(`/clients/${client.id}/contacts`);
  };

  // Permissions
  const { user } = useAuth();
  const isAdmin = user?.role_obj?.name?.toLowerCase() === 'admin' || user?.role?.toLowerCase() === 'admin';
  
  const hasPermission = (moduleName, action) => {
    const perms = user?.permissions || {};
    const key = Object.keys(perms).find(k => k.toLowerCase() === moduleName.toLowerCase());
    return !!(key && perms[key] && perms[key][action]);
  };

  // Form states
  const [clientForm, setClientForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    source: '',
    status: 'active',
    address: '',
    pan_number: ''
  });

  const [detailsForm, setDetailsForm] = useState({
    aadharNumber: '',
    panNumber: '',
    gstNumber: '',
    documentType: '',
    documentNumber: ''
  });

  const [accounts, setAccounts] = useState([]);
  const [accountForm, setAccountForm] = useState({
    bankName: '',
    accountNumber: '',
    accountType: 'savings',
    ifscCode: '',
    branchName: '',
    primaryAccount: false,
    id: null
  });
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [expandedAccount, setExpandedAccount] = useState(null);

  // Validation error states
  const [clientFormErrors, setClientFormErrors] = useState({});
  const [detailsFormErrors, setDetailsFormErrors] = useState({});
  const [accountFormErrors, setAccountFormErrors] = useState({});

  // Validation rules
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // Allow empty or 10-15 digit numbers with optional hyphens/spaces
    if (!phone) return true;
    const phoneRegex = /^[\d\s\-\+\(\)]{10,15}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  const validateAadhar = (aadhar) => {
    if (!aadhar) return true;
    // Aadhar is 12 digits
    return /^\d{12}$/.test(aadhar.replace(/\s/g, ''));
  };

  const validatePAN = (pan) => {
    if (!pan) return true;
    // PAN format: 5 letters, 4 numbers, 1 letter
    return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan);
  };

  const validateGST = (gst) => {
    if (!gst) return true;
    // GST is 15 characters
    return /^[0-9A-Z]{15}$/.test(gst);
  };

  const validateAccountNumber = (accNum) => {
    if (!accNum) return true;
    // Account number should be 8-20 digits
    return /^\d{8,20}$/.test(accNum.replace(/\s/g, ''));
  };

  const validateIFSC = (ifsc) => {
    if (!ifsc) return true;
    // IFSC is 11 characters: 4 letters, 0, 6 alphanumeric
    return /^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc);
  };

  // Validate client form
  const validateClientForm = () => {
    const errors = {};
    
    if (!clientForm.name?.trim()) {
      errors.name = 'Name is required';
    } else if (clientForm.name.length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }

    if (!clientForm.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!validateEmail(clientForm.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (clientForm.phone && !validatePhone(clientForm.phone)) {
      errors.phone = 'Please enter a valid phone number (10-15 digits)';
    }

    if (clientForm.pan_number && !validatePAN(clientForm.pan_number)) {
      errors.pan_number = 'Invalid PAN format (e.g., AAAAA1234A)';
    }

    setClientFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validate details form
  const validateDetailsForm = () => {
    const errors = {};

    if (detailsForm.aadharNumber && !validateAadhar(detailsForm.aadharNumber)) {
      errors.aadharNumber = 'Aadhar must be 12 digits';
    }

    if (detailsForm.panNumber && !validatePAN(detailsForm.panNumber)) {
      errors.panNumber = 'Invalid PAN format (e.g., AAAAA1234A)';
    }

    if (detailsForm.gstNumber && !validateGST(detailsForm.gstNumber)) {
      errors.gstNumber = 'GST must be 15 characters';
    }

    setDetailsFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validate account form
  const validateAccountForm = (form = accountForm) => {
    const errors = {};

    if (!form.bankName?.trim()) {
      errors.bankName = 'Bank name is required';
    }

    if (!form.accountNumber?.trim()) {
      errors.accountNumber = 'Account number is required';
    } else if (!validateAccountNumber(form.accountNumber)) {
      errors.accountNumber = 'Account number must be 8-20 digits';
    }

    if (!form.ifscCode?.trim()) {
      errors.ifscCode = 'IFSC code is required';
    } else if (!validateIFSC(form.ifscCode)) {
      errors.ifscCode = 'Invalid IFSC format (e.g., SBIN0001234)';
    }

    if (!form.branchName?.trim()) {
      errors.branchName = 'Branch name is required';
    }

    setAccountFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Filtered clients based on search (guard against null entries/fields)
  const filteredClients = (clients || []).filter(client => {
    if (!client) return false;
    const q = (searchQuery || '').toLowerCase();
    const name = (client.name || '').toLowerCase();
    const email = (client.email || '').toLowerCase();
    const company = (client.company || '').toLowerCase();
    const phone = (client.phone || '').toLowerCase();
    const source = (client.source || '').toLowerCase();
    const pan = (((client.pan_number || client.clientDetail?.pan_number) || '')).toLowerCase();

    return (
      name.includes(q) ||
      email.includes(q) ||
      company.includes(q) ||
      phone.includes(q) ||
      source.includes(q) ||
      pan.includes(q)
    );
  });

  const totals = useMemo(() => {
    const sums = { totalAmount: 0, paidAmount: 0, pendingAmount: 0 };
    (filteredClients || []).forEach(c => {
      sums.totalAmount += Number(c.totalAmount || 0);
      sums.paidAmount += Number(c.paidAmount || 0);
      sums.pendingAmount += Number(c.pendingAmount || 0);
    });
    return sums;
  }, [filteredClients]);

  // Fetch clients
  useEffect(() => {
    fetchClients();
  }, [startDate, endDate]);

  const fetchClients = async () => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      
      const clientsRes = await api.get('/clients', { params });
      
      const clientsData = clientsRes.data.data;
      
      console.log('[fetchClients] clientsData:', clientsData);
      if (clientsData.length > 0) {
        console.log('[fetchClients] sample client:', clientsData[0]);
      }
      
      setClients(clientsData);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Error fetching clients');
    } finally {
      setLoading(false);
    }
  };

  const handleExportClients = async () => {
    try {
      setExporting(true);
      setError(null);
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (searchQuery) params.q = searchQuery;

      const response = await api.get('/clients/export', {
        params,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `clients-${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to export clients');
    } finally {
      setExporting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all forms
    const isClientFormValid = validateClientForm();
    const isDetailsFormValid = validateDetailsForm();
    
    if (!isClientFormValid || !isDetailsFormValid) {
      setError('Please fix validation errors before submitting');
      return;
    }

    try {
      // Prepare the detail object only if any field has data
      const hasDetails = Object.values(detailsForm).some(value => value !== '');
      // Consider accounts added via the "Add Account" action or the current account form
      const hasSavedAccount = accounts && accounts.length > 0;
      const hasAccountFormValues = Object.values(accountForm).some(value => value !== '' && value !== false && value !== 'savings');

      const payload = {
        ...clientForm,
        pan_number: clientForm.pan_number || null,
        clientDetail: hasDetails ? {
          aadhar_number: detailsForm.aadharNumber,
          gst_number: detailsForm.gstNumber,
          document_type: detailsForm.documentType,
          document_number: detailsForm.documentNumber
        } : null,
        // Prefer saved account (from accounts array) if present; otherwise do not send partial account data
        clientAccount: hasSavedAccount ? {
          bank_name: accounts[0].bank_name,
          account_number: accounts[0].account_number,
          account_type: accounts[0].account_type,
          ifsc_code: accounts[0].ifsc_code,
          branch_name: accounts[0].branch_name,
          primary_account: accounts[0].primary_account
        } : null
      };

      console.log('Sending payload:', payload);
      console.log("selectedClient", selectedClient);

      let response;
      if (selectedClient?.id) {
        response = await api.put(`/clients/${selectedClient.id}`, payload);
      } else {
        response = await api.post('/clients', payload);
      }

      console.log('Server response:', response.data);

      // Reset all forms
      setClientForm({
        name: '',
        email: '',
        phone: '',
        company: '',
        source: '',
        status: 'active',
        address: '',
        notes: '',
        pan_number: ''
      });
      setDetailsForm({
        aadharNumber: '',
        panNumber: '',
        gstNumber: '',
        documentType: '',
        documentNumber: ''
      });
      setAccountForm({
        bankName: '',
        accountNumber: '',
        accountType: 'savings',
        ifscCode: '',
        branchName: '',
        primaryAccount: false
      });
      setClientFormErrors({});
      setDetailsFormErrors({});
      setAccountFormErrors({});
      setSelectedClient(null);
      setActiveTab(0); // Reset to first tab
      fetchClients();
    } catch (err) {
      setError(err.response?.data?.error || 'Error saving client');
    }
  };

  const handleView = async (client) => {
    if (!hasPermission('Client', 'read')) {
      setError('You do not have permission to view clients');
      return;
    }
    setSelectedClient(client);
    setViewMode('view');
    setClientForm({
      name: client.name,
      email: client.email,
      phone: client.phone || '',
      company: client.company || '',
      source: client.source || '',
      status: client.status || 'active',
      address: client.address || '',
      notes: client.notes || '',
      pan_number: client.pan_number || ''
    });

    try {
      // Fetch client details
      const detailsResponse = await api.get(`/clients/${client.id}/details`);
      const details = detailsResponse.data.data || {};
      setDetailsForm({
        aadharNumber: details.aadhar_number || '',
        panNumber: client.pan_number || details.pan_number || '',
        gstNumber: details.gst_number || '',
        documentType: details.document_type || '',
        documentNumber: details.document_number || ''
      });

      // Ensure clientForm.pan_number reflects DB/clientDetail value (fallback)
      setClientForm(prev => ({ ...prev, pan_number: client.pan_number || details.pan_number || '' }));

      // Fetch client accounts
      const accountsResponse = await api.get(`/clients/${client.id}/accounts`);
      setAccounts(accountsResponse.data.data || []);
    } catch (err) {
      console.error('Error fetching client details:', err);
      setError('Error loading client details');
    }
  };

  const handleEdit = async (client) => {
    if (!hasPermission('Client', 'update')) {
      setError('You do not have permission to edit clients');
      return;
    }
    setSelectedClient(client);
    setViewMode('edit');
    setClientForm({
      name: client.name,
      email: client.email,
      phone: client.phone || '',
      company: client.company || '',
      source: client.source || '',
      status: client.status || 'active',
      address: client.address || '',
      notes: client.notes || '',
      pan_number: client.pan_number || ''
    });

    try {
      // Fetch client details
      const detailsResponse = await api.get(`/clients/${client.id}/details`);
      const details = detailsResponse.data.data || {};
      setDetailsForm({
        aadharNumber: details.aadhar_number || '',
        panNumber: client.pan_number || details.pan_number || '',
        gstNumber: details.gst_number || '',
        documentType: details.document_type || '',
        documentNumber: details.document_number || ''
      });

      // Fetch client accounts
      const accountsResponse = await api.get(`/clients/${client.id}/accounts`);
      setAccounts(accountsResponse.data.data || []);
      // Ensure clientForm.pan_number reflects DB/clientDetail value (fallback)
      setClientForm(prev => ({ ...prev, pan_number: client.pan_number || details.pan_number || '' }));
    } catch (err) {
      console.error('Error fetching client details:', err);
      setError('Error loading client details');
    }
  };

  const handleDelete = async (id) => {
    if (!hasPermission('Client', 'delete')) {
      setError('You do not have permission to delete clients');
      return;
    }

    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        setSuccess('');
        await api.delete(`/clients/${id}`);
        setSuccess('Client deleted successfully');
        fetchClients();
      } catch (err) {
        setSuccess('');
        setError(err.response?.data?.error || 'Error deleting client');
      }
    }
  };

  const toggleAccount = (accountId) => {
    setExpandedAccount(expandedAccount === accountId ? null : accountId);
  };

  const handleEditAccount = (account) => {
    setAccountForm({
      bankName: account.bank_name,
      accountNumber: account.account_number,
      accountType: account.account_type,
      ifscCode: account.ifsc_code,
      branchName: account.branch_name,
      primaryAccount: account.primary_account,
      id: account.id
    });
    setShowAccountForm(true);
  };

  const handleSaveAccount = async () => {
    try {
      // Validate account before saving
      const isValid = validateAccountForm(accountForm);
      if (!isValid) {
        setError('Please fix account form errors before saving');
        return;
      }

      if (!selectedClient) {
        // If no client is selected, save the account to the state for later submission
        setAccounts([...accounts, {
          ...accountForm,
          id: Math.random(), // Temporary ID for new accounts
          bank_name: accountForm.bankName,
          account_number: accountForm.accountNumber,
          account_type: accountForm.accountType,
          ifsc_code: accountForm.ifscCode,
          branch_name: accountForm.branchName,
          primary_account: accountForm.primaryAccount
        }]);
      } else {
        // If editing an existing client, save immediately to the backend
        const endpoint = accountForm.id
          ? `/clients/${selectedClient.id}/accounts/${accountForm.id}`
          : `/clients/${selectedClient.id}/accounts`;
        const method = accountForm.id ? 'put' : 'post';

        await api[method](endpoint, {
          bank_name: accountForm.bankName,
          account_number: accountForm.accountNumber,
          account_type: accountForm.accountType,
          ifsc_code: accountForm.ifscCode,
          branch_name: accountForm.branchName,
          primary_account: accountForm.primaryAccount
        });

        // Refresh the accounts list
        const response = await api.get(`/clients/${selectedClient.id}/accounts`);
        setAccounts(response.data.data);
      }

      // Reset form and hide it
      setAccountForm({
        bankName: '',
        accountNumber: '',
        accountType: 'savings',
        ifscCode: '',
        branchName: '',
        primaryAccount: false,
        id: null
      });
      setShowAccountForm(false);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Error saving account');
    }
  };

  const handleDeleteAccount = async (accountId) => {
    if (!window.confirm('Are you sure you want to delete this account?')) return;

    try {
      if (selectedClient) {
        // If editing an existing client, delete from backend
        await api.delete(`/clients/${selectedClient.id}/accounts/${accountId}`);
        const response = await api.get(`/clients/${selectedClient.id}/accounts`);
        setAccounts(response.data.data);
      } else {
        // If creating a new client, just remove from state
        setAccounts(accounts.filter(acc => acc.id !== accountId));
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error deleting account');
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="clients-container">
      {!selectedClient || (selectedClient === null && viewMode === 'edit') ? (
        <>
          <div className="modern-header">
            <div className="header-left">
              <button 
                onClick={() => navigate(-1)}
                className="back-button"
                title="Go back"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
                Back
              </button>
            </div>

            <div className="header-center">
              <h2>Clients</h2>
            </div>

            <div className="header-right">
              {hasPermission('Client', 'create') && (
                <>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    id="client-upload-input"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const file = e.target.files && e.target.files[0];
                      if (!file) return;
                      setError('');
                      setSuccess('');
                      setLoading(true);
                      try {
                        const form = new FormData();
                        form.append('file', file);
                        const res = await api.post('/clients/bulk-upload', form);
                        const created = res.data?.created || 0;
                        const errors = res.data?.errors || [];
                        if (errors.length) {
                          setError(`Created ${created} clients. ${errors.length} rows had errors.`);
                        } else {
                          setSuccess('Bulk upload successful. Created ' + created + ' clients.');
                        }
                        const { data: clientsData } = await api.get('/clients');
                        setClients(clientsData.data);
                      } catch (err) {
                        setError(err.response?.data?.error || err.message || 'Bulk upload failed');
                      } finally {
                        setLoading(false);
                        e.target.value = '';
                      }
                    }}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '6px' }}>
                    <Button 
                      onClick={() => document.getElementById('client-upload-input').click()}
                      className="upload-button"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
                      </svg>
                      Bulk
                    </Button>
                  </div>
                  <div style={{ width: '100%', marginTop: 4, alignSelf: 'flex-start' }}>
                    <a href="/clients-template.xlsx" download className="template-download-link">Sample template</a>
                  </div>
                  <Button
                    onClick={handleExportClients}
                    disabled={exporting || filteredClients.length === 0}
                    className="export-button"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>
                    </svg>
                    {exporting ? 'Exporting...' : 'Export'}
                  </Button>
                  <Button 
                    onClick={() => {
                      setSelectedClient({});
                      setViewMode('add');
                      setClientForm({
                        name: '',
                        email: '',
                        phone: '',
                        company: '',
                        source: '',
                        status: 'active',
                        address: '',
                        notes: ''
                      });
                      setDetailsForm({
                        aadharNumber: '',
                        panNumber: '',
                        gstNumber: '',
                        documentType: '',
                        documentNumber: ''
                      });
                      setAccounts([]);
                      setActiveTab(0);
                    }}
                    className="add-button"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 1V15M1 8H15" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                     Client
                  </Button>
                  
                </>
              )}
            </div>
            
          </div>

          {error && <div className="error-message">{error}</div>}

          {success && <div className="success-message">{success}</div>}

          <div className="filters-bar">
            <div className="search-box-modern">
              <input
                type="text"
                placeholder="Search clients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="date-filters" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ minWidth: 160 }}>
                <DateInput label="From" value={startDate} onChange={val => setStartDate(val)} />
              </div>
              <div style={{ minWidth: 160 }}>
                <DateInput label="To" value={endDate} onChange={val => setEndDate(val)} />
              </div>
            </div>
          </div>

        {isAdmin && (
          <div className="totals-row-amounts" aria-hidden={false}>
            <div className="totals-cell ">
              <div className="totals-label">No. of Clients</div>
              <div className="totals-amount" style={{ color: '#b0650eff' }}>{filteredClients.length}</div>
            </div>
            <div className="totals-cell ">
              <div className="totals-label">Paid</div>
              <div className="totals-amount" style={{ color: '#2e7d32' }}>₹ {totals.paidAmount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
            </div>
            <div className="totals-cell ">
              <div className="totals-label">Pending</div>
              <div className="totals-amount" style={{ color: '#d32f2f'  }}>₹ {totals.pendingAmount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
            </div>
            
            <div className="totals-cell ">
              <div className="totals-label">Total</div>
              <div className="totals-amount" style={{ fontWeight: 'bold', color: '#1632d0ff' }}>₹ {totals.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
            </div>
             <div className="totals-cell "></div>
          </div>
        )}

          <div className="clients-table-wrapper table-responsive">
            <table className="clients-table">
              <thead>
                <tr>
                  <th className="col-company">Company</th>
                  <th className="col-name">Nick Name</th>
                  <th className="col-source">Source</th>
                  <th className="col-pan">PAN</th>
                  <th className="col-status">Status</th>
                  {isAdmin && <th className="col-pending">Pending Amount</th>}
                  {isAdmin && <th className="col-paid">Paid Amount</th>}
                  {isAdmin && <th className="col-total">Total Amount</th>}
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map(client => (
                  <tr key={client.id}>
                    <td className="col-company">{client.company || '-'}</td>
                    <td className="col-name">{client.name}</td>
                    <td className="col-source">{client.source || '-'}</td>
                    <td className="col-pan">{client.pan_number || client.clientDetail?.pan_number || '-'}</td>
                    <td className="col-status">
                      <span className={`status-badge ${client.status}`}>
                        {client.status}
                      </span>
                    </td>
                    {isAdmin && (
                      <td className="col-pending" style={{ color: client.pendingAmount > 0 ? '#d32f2f' : '#2e7d32' }}>
                        ₹ {(client.pendingAmount?.toLocaleString('en-IN', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
}) || 0)}
                        
                      </td>
                    )}
                    {isAdmin && (
                      <td className="col-paid" style={{ color: '#2e7d32' }}>
                        ₹ {(client.paidAmount?.toLocaleString('en-IN', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
}) || 0)}
                      </td>
                    )}
                    {isAdmin && (
                      <td className="col-total" style={{ fontWeight: 'bold' }}>
                        ₹ {(client?.totalAmount?.toLocaleString('en-IN', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
}) || 0)}
                      </td>
                    )}
                    <td className="col-actions">
                      <div className="action-buttons">
                        {hasPermission('Client', 'read') && (
                          <IconButton size="medium" title="View client" onClick={() => handleView(client)}>
                            <svg viewBox="0 0 24 24" fill="none"><path d="M12 5C7 5 2.73 8.11 1 12c1.73 3.89 6 7 11 7s9.27-3.11 11-7c-1.73-3.89-6-7-11-7z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </IconButton>
                        )}
                        {hasPermission('Client', 'update') && (
                          <IconButton size="medium" title="Edit client" onClick={() => handleEdit(client)}>
                            <svg viewBox="0 0 24 24" fill="none"><path d="M3 21v-3.6l11-11L20.6 9 9.6 20H3z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </IconButton>
                        )}
                        {hasPermission('Client', 'read') && (
                          <IconButton size="medium" title="Contacts" onClick={() => navigateToContacts(client)}>
                            <svg viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="14" height="16" rx="2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M7 9h6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M7 13h6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><circle cx="18.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </IconButton>
                        )}

                        {hasPermission('Client', 'read') && (
                          <IconButton size="medium" title="Payments" onClick={() => navigate(`/clients/${client.id}/payments`)} ariaLabel="Payments">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><text fill="currentColor" x="50%" y="50%" dominantBaseline="middle" textAnchor="middle" fontSize="15">₹</text></svg>
                          </IconButton>
                        )}

                        {hasPermission('Client', 'read') && (
                          // pass clientName as state to invoices page for better UX
                          <IconButton size="medium" title="Invoices" onClick={() => navigate(`/clients/${client.id}/invoices`, { state: { clientName: client.name } })} ariaLabel="Invoices">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M21 8V21a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1h13l4 4z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M7 13h10M7 17h6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </IconButton>
                        )}

                        {hasPermission('Client', 'delete') && (
                          <IconButton size="medium" title="Delete client" onClick={() => handleDelete(client.id)}>
                            <svg viewBox="0 0 24 24" fill="none"><path d="M3 6h18" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M8 6v12a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </IconButton>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

          </div>
        </>
      ) : (
        <div className="client-details">
          <div className="details-header">
            <h1>{viewMode === 'add' ? 'Add Client' : viewMode === 'edit' ? 'Edit Client' : 'View Client'}</h1>
            <button onClick={() => {
              setSelectedClient(null);
              setViewMode(null);
            }} className="back-btn">
              Back to List
            </button>
          </div>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit} className="client-form">
            <Tabs selectedIndex={activeTab} onSelect={index => setActiveTab(index)}>
          <TabList>
            <Tab>Basic Info</Tab>
            <Tab>Documents</Tab>
            <Tab>Bank Details</Tab>
          </TabList>

          <TabPanel>
            <div className="form-group">
              <label>Company Name:</label>
              <input
                type="text"
                value={clientForm.company}
                onChange={(e) => setClientForm({...clientForm, company: e.target.value})}
                disabled={viewMode === 'view'}
              />
            </div>
            <div className="form-group">
              <label>Nick Name:</label>
              <input
                type="text"
                value={clientForm.name}
                onChange={(e) => {
                  setClientForm({...clientForm, name: e.target.value});
                  if (e.target.value.trim()) {
                    if (e.target.value.length < 2) {
                      setClientFormErrors({...clientFormErrors, name: 'Name must be at least 2 characters'});
                    } else {
                      setClientFormErrors({...clientFormErrors, name: null});
                    }
                  } else {
                    setClientFormErrors({...clientFormErrors, name: 'Name is required'});
                  }
                }}
                required
                disabled={viewMode === 'view'}
                className={clientFormErrors.name ? 'input-error' : ''}
              />
              {clientFormErrors.name && <span className="error-text">{clientFormErrors.name}</span>}
            </div>

            <div className="form-group">
              <label>Email:</label>
              <input
                type="email"
                value={clientForm.email}
                onChange={(e) => {
                  setClientForm({...clientForm, email: e.target.value});
                  if (e.target.value.trim()) {
                    if (!validateEmail(e.target.value)) {
                      setClientFormErrors({...clientFormErrors, email: 'Please enter a valid email address'});
                    } else {
                      setClientFormErrors({...clientFormErrors, email: null});
                    }
                  } else {
                    setClientFormErrors({...clientFormErrors, email: 'Email is required'});
                  }
                }}
                required
                disabled={viewMode === 'view'}
                className={clientFormErrors.email ? 'input-error' : ''}
              />
              {clientFormErrors.email && <span className="error-text">{clientFormErrors.email}</span>}
            </div>

            <div className="form-group">
              <label>Phone:</label>
              <input
                type="tel"
                value={clientForm.phone}
                onChange={(e) => {
                  setClientForm({...clientForm, phone: e.target.value});
                  if (e.target.value && !validatePhone(e.target.value)) {
                    setClientFormErrors({...clientFormErrors, phone: 'Please enter a valid phone number (10-15 digits)'});
                  } else {
                    setClientFormErrors({...clientFormErrors, phone: null});
                  }
                }}
                disabled={viewMode === 'view'}
                className={clientFormErrors.phone ? 'input-error' : ''}
              />
              {clientFormErrors.phone && <span className="error-text">{clientFormErrors.phone}</span>}
            </div>

            <div className="form-group">
              <label>PAN Number:</label>
              <input
                type="text"
                value={clientForm.pan_number}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  setClientForm({...clientForm, pan_number: value});
                  if (value && !validatePAN(value)) {
                    setClientFormErrors({...clientFormErrors, pan_number: 'Invalid PAN format (e.g., AAAAA1234A)'});
                  } else {
                    setClientFormErrors({...clientFormErrors, pan_number: null});
                  }
                }}
                disabled={viewMode === 'view'}
                className={clientFormErrors.pan_number ? 'input-error' : ''}
                placeholder="AAAAA1234A"
              />
              {clientFormErrors.pan_number && <span className="error-text">{clientFormErrors.pan_number}</span>}
            </div>

            {/* <div className="form-group">
              <label>Company:</label>
              <input
                type="text"
                value={clientForm.company}
                onChange={(e) => setClientForm({...clientForm, company: e.target.value})}
                disabled={viewMode === 'view'}
              />
            </div> */}

            <div className="form-group">
              <label>Source:</label>
              <input
                type="text"
                value={clientForm.source}
                onChange={(e) => setClientForm({...clientForm, source: e.target.value})}
                disabled={viewMode === 'view'}
                placeholder="e.g., web, referral, partner"
              />
            </div>

            <div className="form-group">
              <label>Status:</label>
              <select
                value={clientForm.status}
                onChange={(e) => setClientForm({...clientForm, status: e.target.value})}
                disabled={viewMode === 'view'}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              
            </div>

            <div className="form-group">
              <label>Address:</label>
              <textarea
                value={clientForm.address}
                onChange={(e) => setClientForm({...clientForm, address: e.target.value})}
                disabled={viewMode === 'view'}
              />
            </div>

            <div className="form-group">
              <label>Notes:</label>
              <textarea
                value={clientForm.notes}
                onChange={(e) => setClientForm({...clientForm, notes: e.target.value})}
                disabled={viewMode === 'view'}
              />
            </div>
          </TabPanel>

          <TabPanel>
            <div className="form-group">
              <label>Aadhar Number:</label>
              <input
                type="text"
                value={detailsForm.aadharNumber}
                onChange={(e) => {
                  setDetailsForm({...detailsForm, aadharNumber: e.target.value});
                  if (e.target.value && !validateAadhar(e.target.value)) {
                    setDetailsFormErrors({...detailsFormErrors, aadharNumber: 'Aadhar must be 12 digits'});
                  } else {
                    setDetailsFormErrors({...detailsFormErrors, aadharNumber: null});
                  }
                }}
                disabled={viewMode === 'view'}
                className={detailsFormErrors.aadharNumber ? 'input-error' : ''}
                placeholder="XXXXXXXXXXXX"
              />
              {detailsFormErrors.aadharNumber && <span className="error-text">{detailsFormErrors.aadharNumber}</span>}
            </div>

            {/* PAN moved to Basic Info (clientForm.pan_number) */}

            <div className="form-group">
              <label>GST Number:</label>
              <input
                type="text"
                value={detailsForm.gstNumber}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  setDetailsForm({...detailsForm, gstNumber: value});
                  if (value && !validateGST(value)) {
                    setDetailsFormErrors({...detailsFormErrors, gstNumber: 'GST must be 15 characters'});
                  } else {
                    setDetailsFormErrors({...detailsFormErrors, gstNumber: null});
                  }
                }}
                disabled={viewMode === 'view'}
                className={detailsFormErrors.gstNumber ? 'input-error' : ''}
                placeholder="XXXXXXXXXXX15X"
              />
              {detailsFormErrors.gstNumber && <span className="error-text">{detailsFormErrors.gstNumber}</span>}
            </div>

            <div className="form-group">
              <label>Document Type:</label>
              <input
                type="text"
                value={detailsForm.documentType}
                onChange={(e) => setDetailsForm({...detailsForm, documentType: e.target.value})}
                disabled={viewMode === 'view'}
              />
            </div>

            <div className="form-group">
              <label>Document Number:</label>
              <input
                type="text"
                value={detailsForm.documentNumber}
                onChange={(e) => setDetailsForm({...detailsForm, documentNumber: e.target.value})}
                disabled={viewMode === 'view'}
              />
            </div>
          </TabPanel>

          <TabPanel>
            <div className="accounts-section">
              <div className="accounts-header">
                <h3>Bank Accounts</h3>
                {!(viewMode === 'view') && (
                <button type="button"  onClick={() => {
                  setAccountForm({
                    bankName: '',
                    accountNumber: '',
                    accountType: 'savings',
                    ifscCode: '',
                    branchName: '',
                    primaryAccount: false,
                    id: null
                  });
                  setShowAccountForm(true);
                }} className="add-account-btn">
                  Add Account
                </button>
                )}
              </div>

              {showAccountForm && (
                <div className="account-form">
                  <h4>{accountForm.id ? 'Edit Account' : 'Add New Account'}</h4>
                  <div className="form-group">
                    <label>Bank Name:</label>
                    <input
                      type="text"
                      value={accountForm.bankName}
                      onChange={(e) => {
                        setAccountForm({...accountForm, bankName: e.target.value});
                        if (e.target.value.trim()) {
                          setAccountFormErrors({...accountFormErrors, bankName: null});
                        } else {
                          setAccountFormErrors({...accountFormErrors, bankName: 'Bank name is required'});
                        }
                      }}
                      required
                      disabled={viewMode === 'view'}
                      className={accountFormErrors.bankName ? 'input-error' : ''}
                    />
                    {accountFormErrors.bankName && <span className="error-text">{accountFormErrors.bankName}</span>}
                  </div>

                  <div className="form-group">
                    <label>Account Number:</label>
                    <input
                      type="text"
                      value={accountForm.accountNumber}
                      onChange={(e) => {
                        setAccountForm({...accountForm, accountNumber: e.target.value});
                        if (e.target.value.trim()) {
                          if (!validateAccountNumber(e.target.value)) {
                            setAccountFormErrors({...accountFormErrors, accountNumber: 'Account number must be 8-20 digits'});
                          } else {
                            setAccountFormErrors({...accountFormErrors, accountNumber: null});
                          }
                        } else {
                          setAccountFormErrors({...accountFormErrors, accountNumber: 'Account number is required'});
                        }
                      }}
                      required
                      disabled={viewMode === 'view'}
                      className={accountFormErrors.accountNumber ? 'input-error' : ''}
                      placeholder="XXXXXXXX-XXXX"
                    />
                    {accountFormErrors.accountNumber && <span className="error-text">{accountFormErrors.accountNumber}</span>}
                  </div>

                  <div className="form-group">
                    <label>Account Type:</label>
                    <select
                      value={accountForm.accountType}
                      onChange={(e) => setAccountForm({...accountForm, accountType: e.target.value})}
                      required
                      disabled={viewMode === 'view'}
                    >
                      <option value="savings">Savings</option>
                      <option value="current">Current</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>IFSC Code:</label>
                    <input
                      type="text"
                      value={accountForm.ifscCode}
                      onChange={(e) => {
                        const value = e.target.value.toUpperCase();
                        setAccountForm({...accountForm, ifscCode: value});
                        if (value.trim()) {
                          if (!validateIFSC(value)) {
                            setAccountFormErrors({...accountFormErrors, ifscCode: 'Invalid IFSC format (e.g., SBIN0001234)'});
                          } else {
                            setAccountFormErrors({...accountFormErrors, ifscCode: null});
                          }
                        } else {
                          setAccountFormErrors({...accountFormErrors, ifscCode: 'IFSC code is required'});
                        }
                      }}
                      required
                      disabled={viewMode === 'view'}
                      className={accountFormErrors.ifscCode ? 'input-error' : ''}
                      placeholder="SBIN0001234"
                    />
                    {accountFormErrors.ifscCode && <span className="error-text">{accountFormErrors.ifscCode}</span>}
                  </div>

                  <div className="form-group">
                    <label>Branch Name:</label>
                    <input
                      type="text"
                      value={accountForm.branchName}
                      onChange={(e) => {
                        setAccountForm({...accountForm, branchName: e.target.value});
                        if (e.target.value.trim()) {
                          setAccountFormErrors({...accountFormErrors, branchName: null});
                        } else {
                          setAccountFormErrors({...accountFormErrors, branchName: 'Branch name is required'});
                        }
                      }}
                      required
                      disabled={viewMode === 'view'}
                      className={accountFormErrors.branchName ? 'input-error' : ''}
                    />
                    {accountFormErrors.branchName && <span className="error-text">{accountFormErrors.branchName}</span>}
                  </div>

                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={accountForm.primaryAccount}
                        onChange={(e) => setAccountForm({...accountForm, primaryAccount: e.target.checked})}
                        disabled={viewMode === 'view'}
                      />
                      Primary Account
                    </label>
                  </div>
                  {/* if viewMode is not 'view' then add buttons */}
                  {!(viewMode === 'view') && (
                  <div className="form-actions">
                    {/* <button type="button" onClick={handleSaveAccount}>
                      {accountForm.id ? 'Update Account' : 'Add Account'}
                    </button> */}
                    <button type="button" onClick={() => setShowAccountForm(false)} className="cancel-btn">
                      Cancel
                    </button>
                  </div>
                  )}
                </div>
              )}

              <div className="accounts-list">
                {accounts
                  .sort((a, b) => (b.primary_account ? 1 : -1))
                  .map((account, index) => (
                  <div key={account.id} className="account-item">
                    <div className="account-header" onClick={() => toggleAccount(account.id)}>
                      <div>
                        <strong>{account.bank_name}</strong>
                        {account.primary_account && <span className="primary-badge">Primary</span>}
                      </div>
                      <span className="account-number">****{account.account_number.slice(-4)}</span>
                    </div>
                    {expandedAccount === account.id && (
                      <div className="account-details">
                        <p><strong>Account Type:</strong> {account.account_type}</p>
                        <p><strong>IFSC Code:</strong> {account.ifsc_code}</p>
                        <p><strong>Branch:</strong> {account.branch_name}</p>
                        {!(viewMode === 'view') && (
                        <div className="account-actions">
                          <IconButton size="medium" title="Edit account" onClick={() => handleEditAccount(account)}>
                            <svg viewBox="0 0 24 24" fill="none"><path d="M3 21v-3.6l11-11L20.6 9 9.6 20H3z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </IconButton>

                          <IconButton size="medium" title="Delete account" onClick={() => handleDeleteAccount(account.id)} className="delete-btn">
                            <svg viewBox="0 0 24 24" fill="none"><path d="M3 6h18" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M8 6v12a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </IconButton>
                        </div>
                        )}
                        {(viewMode === 'view') && (
                        <div className="account-actions">
                          <IconButton size="medium" title="View account" onClick={() => handleEditAccount(account)}>
                            <svg viewBox="0 0 24 24" fill="none"><path d="M12 5C7 5 2.73 8.11 1 12c1.73 3.89 6 7 11 7s9.27-3.11 11-7c-1.73-3.89-6-7-11-7z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </IconButton>
                        </div>
                        )}

                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </TabPanel>
        </Tabs>
        {!(viewMode === 'view') && (
        <div className="form-actions">
          {/* if viewMode is not 'view' then add buttons */}
          <button type="submit">{selectedClient?.id ? 'Update Client' : 'Add Client'}</button>
          {selectedClient && (
            <button type="button" onClick={() => {
              setSelectedClient(null);
              setClientForm({
                name: '',
                email: '',
                phone: '',
                company: '',
                source: '',
                status: 'active',
                address: '',
                notes: ''
              });
              setDetailsForm({
                aadharNumber: '',
                panNumber: '',
                gstNumber: '',
                documentType: '',
                documentNumber: ''
              });
              setAccountForm({
                bankName: '',
                accountNumber: '',
                accountType: 'savings',
                ifscCode: '',
                branchName: '',
                primaryAccount: false
              });
            }}>Cancel</button>
          )}
        </div>
        )}
          </form>
        </div>
      )}



      <style jsx>{`
        .clients-container {
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .modern-header {
          background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
          border-bottom: 1px solid #e1e4e8;
          margin: -20px -20px 20px;
          padding: 20px 24px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-radius: 12px 12px 0 0;
        }

        .header-left {
          display: flex;
          align-items: center;
        }

        .back-button {
          display: flex;
          align-items: center;
          gap: 6px;
          background-color: #6c757d;
          color: white;
          border: none;
          padding: 10px 16px;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
          font-size: 14px;
          transition: all 200ms ease;
        }

        .back-button:hover {
          background-color: #5a6268;
          transform: translateY(-1px);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
        }

        .back-button:active {
          transform: translateY(0);
        }

        .header-center {
          flex: 1;
          text-align: center;
        }

        .header-center h2 {
          margin: 0;
          color: #172B4D;
          font-size: 28px;
          font-weight: 700;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .upload-button {
          background-color: #ff9f1c !important;
          color: white !important;
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 16px !important;
          border-radius: 6px !important;
          font-weight: 600 !important;
          font-size: 14px !important;
          transition: all 200ms ease !important;
          border: none !important;
          cursor: pointer !important;
        }

        .upload-button:hover {
          background-color: #e88d1f !important;
          transform: translateY(-1px);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1) !important;
        }

        .template-download-link {
          display: inline-block;
          color: #0057b7;
          text-decoration: underline;
          font-size: 12px;
          margin-top: 30px;
          position: absolute;
          margin-left: -90px;
        }

        .template-download-link:hover {
          color: #003a7a;
        }

        .export-button {
          background-color: #2e7d32 !important;
          color: white !important;
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 16px !important;
          border-radius: 6px !important;
          font-weight: 600 !important;
          font-size: 14px !important;
          transition: all 200ms ease !important;
          border: none !important;
          cursor: pointer !important;
        }

        .export-button:hover:not(:disabled) {
          background-color: #256628 !important;
          transform: translateY(-1px);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1) !important;
        }

        .export-button:disabled {
          opacity: 0.6;
          cursor: not-allowed !important;
        }

        .add-button {
          background-color: #00b359 !important;
          color: white !important;
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 16px !important;
          border-radius: 6px !important;
          font-weight: 600 !important;
          font-size: 14px !important;
          transition: all 200ms ease !important;
          border: none !important;
          cursor: pointer !important;
        }

        .add-button:hover {
          background-color: #008c3a !important;
          transform: translateY(-1px);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1) !important;
        }

        .search-box-modern {
          margin-bottom: 20px;
        }

        .search-box-modern input {
          width: 100%;
          max-width: 400px;
          padding: 10px 16px;
          border: 1px solid #e1e4e8;
          border-radius: 8px;
          font-size: 14px;
          transition: all 200ms ease;
        }

        .search-box-modern input:focus {
          outline: none;
          border-color: #0066cc;
          box-shadow: 0 0 0 3px rgba(0, 102, 204, 0.1);
        }

        .filters-bar {
          display: flex;
          gap: 20px;
          align-items: center;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }

        .date-filters {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .date-filters label {
          font-size: 14px;
          color: #6B778C;
          white-space: nowrap;
        }

        .date-filters input {
          padding: 8px 12px;
          border: 1px solid #e1e4e8;
          border-radius: 8px;
          font-size: 14px;
          transition: all 200ms ease;
        }

        .date-filters input:focus {
          outline: none;
          border-color: #0066cc;
          box-shadow: 0 0 0 3px rgba(0, 102, 204, 0.1);
        }

        .table-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .search-box input {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          width: 300px;
          font-size: 14px;
        }


        table {
          width: 100%;
          border-collapse: collapse;
        }

        th, td {
          padding: 12px 15px;
          text-align: left;
          border-bottom: 1px solid #ddd;
        }

        th {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          font-weight: 600;
          color: #172B4D;
        }

        tr:hover {
          background-color: #f8f9fa;
        }

        .status-badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          text-transform: capitalize;
        }

        .status-badge.active {
          background-color: #e3fcef;
          color: #00875a;
        }

        .status-badge.inactive {
          background-color: #ffebe6;
          color: #de350b;
        }

        .action-buttons {
          display: flex;
          gap: 10px;
          align-items: center;
        }

        .action-buttons button {
          padding: 6px 12px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
        }

        .action-buttons .icon-button {
          padding: 0;
          border-radius: 8px;
        }

        .action-buttons .icon-button:hover {
          background: rgba(0,0,0,0.04);
        }

        .contacts-btn {
          background-color: #ffb400;
          color: white;
        }

        .view-btn {
          background-color: #2684ff;
          color: white;
        }

        .edit-btn {
          background-color: #00875a;
          color: white;
        }

        .delete-btn {
          background-color: #de350b;
          color: white;
        }

        .error-message {
          color: #de350b;
          background-color: #ffebe6;
          padding: 12px;
          border-radius: 4px;
          margin-bottom: 20px;
        }

        .success-message {
          color: #216E4E;
          background-color: #DFFCF0;
          padding: 12px;
          border-radius: 4px;
          margin-bottom: 20px;
        }

        .client-form {
          background: white;
          padding: 20px;
          border-radius: 8px;
          margin-bottom: 30px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .form-group {
          margin-bottom: 15px;
        }

        .form-group label {
          display: block;
          margin-bottom: 5px;
          font-weight: 500;
        }

        .form-group input,
        .form-group textarea,
        .form-group select {
          width: 100%;
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
          background-color: ${props => props.viewMode === 'view' ? '#f4f5f7' : 'white'};
        }

        .form-group input.input-error,
        .form-group textarea.input-error,
        .form-group select.input-error {
          border-color: #de350b;
          background-color: #fff5f3;
        }

        .error-text {
          display: block;
          color: #de350b;
          font-size: 12px;
          margin-top: 4px;
          font-weight: 500;
        }

        .form-group input[type="checkbox"] {
          width: auto;
          margin-right: 8px;
        }

        .form-actions {
          margin-top: 20px;
          display: flex;
          gap: 10px;
        }

        button {
          padding: 8px 16px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 500;
        }

        button:hover {
          opacity: 0.9;
        }

        .details-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .back-btn {
          padding: 8px 16px;
          background-color: #f4f5f7;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }

        :global(.react-tabs__tab-list) {
          border-bottom: 1px solid #ddd;
          margin: 0 0 20px;
          padding: 0;
          display: flex;
          gap: 5px;
        }

        :global(.react-tabs__tab) {
          display: inline-block;
          border: 1px solid transparent;
          border-bottom: none;
          bottom: -1px;
          position: relative;
          list-style: none;
          padding: 8px 15px;
          cursor: pointer;
          border-radius: 5px 5px 0 0;
        }

        :global(.react-tabs__tab--selected) {
          background: #fff;
          border-color: #ddd;
          border-bottom: 1px solid #fff;
          margin-bottom: -1px;
        }

        :global(.react-tabs__tab-panel) {
          display: none;
          padding: 20px 0;
        }

        :global(.react-tabs__tab-panel--selected) {
          display: block;
        }

        .accounts-section {
          margin-top: 20px;
        }

        .accounts-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .add-account-btn {
          background-color: #00875a;
          color: white;
        }

        .account-form {
          background-color: #f8f9fa;
          padding: 20px;
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .accounts-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .account-item {
          border: 1px solid #ddd;
          border-radius: 8px;
          overflow: hidden;
        }

        .account-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px;
          background-color: #f8f9fa;
          cursor: pointer;
        }

        .account-header:hover {
          background-color: #e9ecef;
        }

        .account-details {
          padding: 15px;
          border-top: 1px solid #ddd;
        }

        .account-actions {
          display: flex;
          gap: 10px;
          margin-top: 10px;
        }

        .account-number {
          color: #666;
          font-family: monospace;
        }

        .primary-badge {
          background-color: #00875a;
          color: white;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 12px;
          margin-left: 10px;
        }

        .cancel-btn {
          background-color: #f4f5f7;
          color: #42526e;
        }

        /* Table Responsive Styling */
        .clients-table-wrapper {
          width: 100%;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        .clients-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
        }

        .clients-table th,
        .clients-table td {
          padding: 12px 10px;
          text-align: left;
          border-bottom: 1px solid #e0e0e0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: normal;
          word-wrap: break-word;
        }

        .clients-table th {
          background: linear-gradient(135deg, #f8f9fa 0%, #f0f1f3 100%);
          font-weight: 600;
          color: #333;
          position: sticky;
          top: 0;
          z-index: 10;
          font-size: 13px;
        }

        .clients-table td {
          font-size: 14px;
        }

        .clients-table tbody tr:hover {
          background-color: rgba(0, 102, 204, 0.04);
        }

        /* Totals row aligned to table columns */
        .totals-row {
          display: grid;
          grid-template-columns: 15% 14% 10% 12% 10% 12% 12% 12% 13%;
          gap: 0;
          align-items: center;
          padding: 8px 10px;
          border-bottom: 1px solid #e6e6e6;
          background: linear-gradient(180deg, #fafafa 0%, #fff 100%);
          margin-bottom: 8px;
          border-radius: 8px;
        }

        /* Totals row aligned to table columns */
        .totals-row-amounts {
          display: grid;
          grid-template-columns: 15% 25% 25% 25% 10%;
          gap: 0;
          align-items: center;
          padding: 8px 10px;
          border-bottom: 1px solid #e6e6e6;
          background: linear-gradient(180deg, #fafafa 0%, #fff 100%);
          margin-bottom: 8px;
          border-radius: 8px;
        }

        .totals-cell {
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .totals-label {
          font-size: 12px;
          color: #6b778c;
          margin-bottom: 4px;
          text-align: center;
        }

        .totals-amount {
          font-weight: 700;
          text-align: right;
          color: #172b4d;
          text-align: center;
        }

        /* Column Width Management */
        .col-company { width: 15%; min-width: 120px; }
        .col-name { width: 14%; min-width: 110px; }
        .col-source { width: 10%; min-width: 80px; }
        .col-pan { width: 12%; min-width: 100px; }
        .col-status { width: 10%; min-width: 90px; }
        .col-pending { width: 12%; min-width: 100px; }
        .col-paid { width: 12%; min-width: 100px; }
        .col-total { width: 12%; min-width: 100px; }
        .col-actions { width: 13%; min-width: 120px; }

        .action-buttons {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }

        .status-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          white-space: nowrap;
        }

        .status-badge.active {
          background-color: #e8f5e9;
          color: #2e7d32;
        }

        .status-badge.inactive {
          background-color: #ffebee;
          color: #c62828;
        }

        /* Tablet View - Adjust column widths */
        @media (max-width: 1024px) {
          .clients-table th,
          .clients-table td {
            padding: 10px 8px;
            font-size: 13px;
          }

          .col-company { width: 16%; }
          .col-name { width: 15%; }
          .col-source { width: 11%; }
          .col-pan { width: 13%; }
          .col-status { width: 11%; }
          .col-pending { width: 11%; }
          .col-paid { width: 11%; }
          .col-total { width: 11%; }
          .col-actions { width: 11%; min-width: 100px; }
        }

        /* Mobile View - Stack and adjust */
        @media (max-width: 768px) {
          .clients-table-wrapper {
            overflow-x: auto;
            margin: 0 -16px;
            padding: 0 16px;
          }

          .clients-table {
            table-layout: auto;
            min-width: 700px;
          }

          .clients-table th,
          .clients-table td {
            padding: 8px 6px;
            font-size: 12px;
          }

          .col-company { width: 18%; min-width: 100px; }
          .col-name { width: 16%; min-width: 90px; }
          .col-source { width: 12%; min-width: 70px; }
          .col-pan { width: 14%; min-width: 90px; }
          .col-status { width: 12%; min-width: 80px; }
          .col-pending { width: 13%; min-width: 85px; }
          .col-paid { width: 13%; min-width: 85px; }
          .col-total { width: 13%; min-width: 85px; }
          .col-actions { width: 13%; min-width: 100px; }

          .action-buttons {
            gap: 4px;
          }
        }

        /* Small Mobile - Further optimization */
        @media (max-width: 480px) {
          .clients-table {
            min-width: 600px;
          }

          .clients-table th,
          .clients-table td {
            padding: 6px 4px;
            font-size: 11px;
          }

          .col-company { width: 20%; min-width: 80px; }
          .col-name { width: 17%; min-width: 75px; }
          .col-source { width: 12%; min-width: 60px; }
          .col-pan { width: 15%; min-width: 80px; }
          .col-status { width: 12%; min-width: 70px; }
          .col-pending { width: 12%; min-width: 75px; }
          .col-paid { width: 12%; min-width: 75px; }
          .col-total { width: 12%; min-width: 75px; }
          .col-actions { width: 12%; min-width: 80px; }
        } 
      `}</style>
    </div>
  );
};


export default Clients;
